import AnnualReportForm from "../../../model/AnnualReportForm";
import {useState} from "react";

const APPLICANT_CODE_REFERENCES_QUESTION_TEXT = `Please share the URL links for any code used to produce your 
      results—whether published or unpublished—and ensure all repositories are listed, regardless of whether they are 
      associated with a publication. We strongly encourage the use of open-source platforms such as GitHub, GitLab, or 
      Bitbucket. In addition, please provide details of any other research outputs related to your use of UK Biobank 
      data, including websites, patents, and the location of GWAS summary statistics.`;
const THIRD_PARTY_CODE_HELP_URL = `https://community.ukbiobank.ac.uk/hc/en-gb/articles/28027475352221-Use-of-third-party-code-sharing-repositories-e-g-GitHub`;
const APPLICANT_EXTRA_INFO = <>
   In addition, UK Biobank requires information regarding the use of code repositories by all researchers listed on the
   project from the relevant institution. Further details on the use of third-party code-sharing platforms (e.g. GitHub)
   are available here: <a href={THIRD_PARTY_CODE_HELP_URL}>Use of third-party code-sharing repositories</a>.
</>;
const COLLAB_CODE_REFERENCES_QUESTION_TEXT = `Please share the URL links for any code used to produce your published results (preferably hosted 
      in an open-source repository like GitHub), along with details of other research outputs such as websites, patents,
      or the location of GWAS summary statistics. `;
const MTA_PATENT_REMINDER = `As stated in the Material Transfer Agreement (MTA), the Applicant PI must provide UK Biobank 
      with a copy of any patents whose claims cover, or are intended to cover, an Applicant Generated Invention within 2 months 
      of the patent rights being granted.`;

/**
 * Props for this component
 */
interface CodeReferencesQuestionProps {
   form: AnnualReportForm,
   isLocked: boolean
}

/**
 * Collaborator Annual Report question regarding a url link for any code used in this project.
 *
 * Used in: {@link CollaboratorAnnualReportForm|Collaborator annual report}
 *
 * @param form An {@link AnnualReportForm} object with a saved report (Could be empty if it's a new report)
 * @param isLocked A boolean to indicate if the report is locked for editing or not
 */
export default function CodeReferencesQuestion({form, isLocked}: CodeReferencesQuestionProps) {
   const [links, setLinks] = useState<string[]>(splitLinks(form.codeReferences, isLocked));

   /**
    * When a row is updated, it will store the new value in its respective element. If the element being updated
    * was the empty one (The array will always have one), it will create a new element at the end of the array.
    *
    * @param index The index of the Published Paper in the array
    * @param value The new value of the field
    */
   const updateLinks = (index: number, value: string): void => {
      // Update entry
      const temp: string[] = [...links];
      temp[index] = value;

      // If it's the last paper and user starts typing, add a new one
      if (index === temp.length - 1 && value !== '') {
         temp.push("");
      }

      form.codeReferences = mergeLinks(temp);
      setLinks(temp);
   }

   /**
    * Remove a link from the array
    *
    * @param index The index of the element we wish to delete
    */
   const removeLink = (index: number): void => {
      const temp: string[] = [...links];
      temp.splice(index, 1);

      form.codeReferences = mergeLinks(temp);
      setLinks(temp);
   }

   return <div id="linksQ" className="annual-report-question">
      <label id="linksQMainText" className="question-text">{(form.reportType === 1)?
         APPLICANT_CODE_REFERENCES_QUESTION_TEXT:
         COLLAB_CODE_REFERENCES_QUESTION_TEXT}</label>
      {form.reportType === 1 && <span id="publicInterestQResourceText" className="extra-info">
                  {APPLICANT_EXTRA_INFO}
               </span>}
      {/*Third Party processors' entries*/}
      {links.map((link: string, index) => (
         <div id={`linksQLink_${index}`} key={index}
              className="multi-entry-question">
            <div id={`linksQEntryArea_${index}`}>
               <input
                  id={`linksQLinkName_${index}`}
                  type="text"
                  className="form-control"
                  placeholder="URL"
                  value={link}
                  disabled={isLocked}
                  onChange={(e) => updateLinks(index, e.target.value)}
               />
            </div>
            {/*Show delete button if this is not the empty element*/}
            {(link !== '' && !isLocked) ?
               (<button id={`linksQLinkRemove_${index}`}
                        type="button"
                        className="btn-close"
                        aria-label="Close"
                        onClick={() => removeLink(index)}></button>
               ) : null
            }
         </div>
      ))}
      {form.reportType === 1 && <span id="publicInterestQResourceText" className="extra-info">
                  {MTA_PATENT_REMINDER}
               </span>}
   </div>;
}

/**
 * This method will gather the data from the links and merge it into a single string
 *
 * @param links The array of links
 */
function mergeLinks(links: string[]): string {
   let stringWithLinks: string = "";

   for (let i = 0; i < links.length - 1; i++) {
      // Don't include the ';' at the beginning
      if (i === 0) {
         stringWithLinks = `${encodeURIComponent(links[i])}`;
         continue;
      }
      stringWithLinks = `${stringWithLinks};${encodeURIComponent(links[i])}`;
   }

   return stringWithLinks;
}


/**
 * This function will separate the links associated with this report
 *
 * @param formString The links on the form (Can be null)
 * @param isLocked A boolean to show if the report is blocked or not so we can show the empty row
 */
function splitLinks(formString: string | null, isLocked: boolean): string[] {
   let arrayOfLinks: string[] = [];

   if (formString) {
      arrayOfLinks = formString.split(";").map(decodeURIComponent);
   }

   if (!isLocked) {
      arrayOfLinks.push("");
   }

   return arrayOfLinks;
}
